import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Medal, Star, Trophy, X, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';

const BadgeNotificationContext = createContext(null);

const TIER_STYLES = {
  bronze: {
    bg: 'from-amber-600 to-amber-800',
    border: 'border-amber-500',
    glow: 'shadow-amber-500/50'
  },
  silver: {
    bg: 'from-gray-300 to-gray-500',
    border: 'border-gray-400',
    glow: 'shadow-gray-400/50'
  },
  gold: {
    bg: 'from-yellow-400 to-yellow-600',
    border: 'border-yellow-500',
    glow: 'shadow-yellow-500/50'
  },
  diamond: {
    bg: 'from-cyan-400 to-blue-500',
    border: 'border-cyan-400',
    glow: 'shadow-cyan-400/50'
  }
};

function BadgeNotificationPopup({ badge, onClose }) {
  const tier = badge?.tier || 'bronze';
  const styles = TIER_STYLES[tier] || TIER_STYLES.bronze;

  useEffect(() => {
    // Fire confetti on badge earn
    const duration = 2000;
    const end = Date.now() + duration;

    const colors = tier === 'gold' ? ['#fbbf24', '#f59e0b'] 
                 : tier === 'diamond' ? ['#22d3ee', '#3b82f6']
                 : tier === 'silver' ? ['#9ca3af', '#6b7280']
                 : ['#d97706', '#b45309'];

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: colors
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: colors
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());

    // Auto close after 5 seconds
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose, tier]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5, y: -100 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.5, y: -100 }}
      className="fixed top-20 left-1/2 -translate-x-1/2 z-[100]"
    >
      <div className={`relative bg-gradient-to-br ${styles.bg} rounded-2xl p-1 shadow-2xl ${styles.glow}`}>
        {/* Sparkle effects */}
        <div className="absolute -top-2 -left-2">
          <Sparkles className="h-6 w-6 text-white animate-pulse" />
        </div>
        <div className="absolute -top-2 -right-2">
          <Sparkles className="h-6 w-6 text-white animate-pulse" />
        </div>
        
        <div className="bg-zinc-900/90 backdrop-blur-xl rounded-xl p-6 min-w-[320px]">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 p-1 text-zinc-500 hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
          
          {/* Header */}
          <div className="text-center mb-4">
            <motion.div
              initial={{ rotate: -10, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1 bg-gradient-to-r from-[#02b5ff]/20 to-[#ff025f]/20 rounded-full border border-[#02b5ff]/30 mb-2"
            >
              <Trophy className="h-4 w-4 text-[#02b5ff]" />
              <span className="text-sm font-bold text-white">ACHIEVEMENT UNLOCKED!</span>
            </motion.div>
          </div>
          
          {/* Badge Icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', delay: 0.3, bounce: 0.5 }}
            className="flex justify-center mb-4"
          >
            <div className={`w-24 h-24 rounded-full bg-gradient-to-br ${styles.bg} ${styles.border} border-4 flex items-center justify-center shadow-lg ${styles.glow}`}>
              <span className="text-5xl">{badge?.icon || '🏅'}</span>
            </div>
          </motion.div>
          
          {/* Badge Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center"
          >
            <h3 className="text-xl font-bold text-white mb-1">{badge?.name || 'New Badge'}</h3>
            <p className="text-sm text-zinc-400 mb-3">{badge?.description || 'You earned a new achievement!'}</p>
            
            <div className="flex items-center justify-center gap-4">
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase bg-gradient-to-r ${styles.bg} text-white`}>
                {tier}
              </span>
              <span className="text-[#02b5ff] font-bold">
                +{badge?.points || 0} points
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

export function BadgeNotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const showBadgeNotification = useCallback((badge) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, badge }]);
  }, []);

  const closeBadgeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const showMultipleBadges = useCallback((badges) => {
    // Show badges one at a time with delay
    badges.forEach((badge, index) => {
      setTimeout(() => {
        showBadgeNotification(badge);
      }, index * 3000);
    });
  }, [showBadgeNotification]);

  return (
    <BadgeNotificationContext.Provider value={{ showBadgeNotification, showMultipleBadges }}>
      {children}
      <AnimatePresence>
        {notifications.map(({ id, badge }) => (
          <BadgeNotificationPopup
            key={id}
            badge={badge}
            onClose={() => closeBadgeNotification(id)}
          />
        ))}
      </AnimatePresence>
    </BadgeNotificationContext.Provider>
  );
}

export function useBadgeNotification() {
  const context = useContext(BadgeNotificationContext);
  if (!context) {
    throw new Error('useBadgeNotification must be used within a BadgeNotificationProvider');
  }
  return context;
}

export default BadgeNotificationProvider;
